import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Title, Table, Box, Text } from '@nerdwallet/currency';
import Composition from '@nerdwallet/react-composition';
import nds from '@nerdwallet/base-styles/classes';

import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import TableFooter from '../table-footer/table-footer';
import ExpandingTable from './subcomponents/expanding-table/expanding-table';
import Row from '../row/row';
import {
  repeatable,
  stringSelectSpec,
  anchorIdInput,
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
  stringInputSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import * as componentLibrary from './table-subcomponent-lib';
import {
  addFeaturedBadge,
  builderInputToCompositionFormat,
  enhanceProps,
  mergeColumnConfigs,
  filterColumnConfig,
} from './config-helpers';
import { PRODUCT_TABLE_TYPES } from '../../lib/constants';
import withDataDependency from '../../lib/with-data-dependency-wrapper';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import { getSlug } from '~/server/config-fetcher';

// preset configs
import checking from './configs/checking';
import eduProsCons from './configs/edu-pros-cons';
import investing from './configs/investing';
import investingProsCons from './configs/investing-pros-cons';
import savings from './configs/savings';
import test from '../../../test/mock-data/universal-product-table-test-config';

import styles from './universal-product-table.module.less';
import { unpackDataDependencyIds } from '../../lib/helpers';
import SegmentImpression from '../segment-impression';
import { segmentImpressionId } from '../../lib/segment-analytics';

export const configs = {
  savings,
  checking,
  eduProsCons,
  investing,
  investingProsCons,
  test, // config for our unit tests
};

const getPresetColumnConfig = (tableType, id = '') => {
  const presetColumnConfig = _.get(configs, [tableType, 'columns'], () => []);
  return presetColumnConfig(id);
};

const getColId = (column, index) =>
  _.camelCase(_.get(column, 'name')) || `col-${index}`;

const columnMetadata = (columns) =>
  columns.map((col, index) => ({
    id: getColId(col, index),
    heading: _.get(col, 'name'),
    width: _.get(col, 'width'),
    hideOnMobile: _.get(col, 'hideOnMobile'),
    mobileOrder: _.get(col, 'mobileOrder'),
  }));

const UniversalProductTable = ({
  anchorId,
  columns,
  featuredProducts,
  subtitle,
  tableType,
  title,
  // tableFooter props
  tableFooterButtonText,
  tableFooterButtonLink,
  tableFooterSubText,
  tableFooterSubTextLink,
  products,
  requestedIds,
}) => {
  const buildRow = ({ rowData, index }) => {
    if (!rowData) {
      return null;
    }

    const id = _.get(rowData, 'id');

    // merge table-type config with table-instance config
    const mergedColumns = mergeColumnConfigs(
      builderInputToCompositionFormat(columns), // map the builder input to match the preset's
      getPresetColumnConfig(tableType, id)
    );

    // enhance the column config so some commonly-used data gets passed down by default, i.e. marketplaceEntity
    const enhancedColumns = enhanceProps(mergedColumns, rowData, index);

    // if product is featured, we need to add the badge and change the color of the row
    const featuredInfo = _.find(
      featuredProducts,
      (featuredProduct) => featuredProduct.id === id
    );

    const columnsWithFeatured = featuredInfo
      ? addFeaturedBadge(
          enhancedColumns,
          _.get(featuredInfo, 'label', 'Featured')
        )
      : enhancedColumns;

    const className = featuredInfo ? nds('background-blue-lightest') : '';

    const row = {
      key: _.get(rowData, 'product.institution.name', `row-${index}`),
      className,
    };
    columnsWithFeatured.forEach((col, i) => {
      row[getColId(col, i)] = (
        <Composition
          componentLibrary={componentLibrary}
          componentDocument={_.get(col, 'elements', [])}
        />
      );
    });

    return row;
  };

  const tableComponentType = _.get(
    configs,
    [tableType, 'tableComponent'],
    PRODUCT_TABLE_TYPES.STANDARD
  );
  const isStandardTable = tableComponentType === PRODUCT_TABLE_TYPES.STANDARD;
  const TableComponent = isStandardTable ? Table : ExpandingTable;
  const { pageViewId } = useAnalyticsPageView();

  return (
    <Row
      id={anchorId}
      // standard table should use our standard max widths
      centeredInParent={isStandardTable}
      standardMaxWidths={isStandardTable}
      // expanding table gets custom widths/margins
      className={!isStandardTable && styles.fullWidthMobile}
      dataIntegration="universal-product-table"
    >
      {title && (
        <Title
          component="h1"
          size="small"
          className={styles.universalProductTableTitle}
        >
          {title}
        </Title>
      )}
      {subtitle && (
        <Box className={nds('text-align-center', 'padding-top-3')}>
          <Text>{subtitle}</Text>
        </Box>
      )}
      <div className={nds('margin-top-4')}>
        <SegmentImpression
          eventName="Universal-product-table Viewed"
          eventProps={{
            impression_id: segmentImpressionId,
            section_name: 'universal_product_table',
            pageview_id: pageViewId,
          }}
        >
          <TableComponent
            columns={columnMetadata(
              mergeColumnConfigs(columns, getPresetColumnConfig(tableType))
            )}
            data={requestedIds
              ?.map((id, index) =>
                buildRow({
                  rowData: _.get(products, id),
                  index,
                })
              )
              .filter(Boolean)}
          />
        </SegmentImpression>
      </div>
      <TableFooter
        tableFooterButtonText={tableFooterButtonText}
        tableFooterButtonLink={tableFooterButtonLink}
        tableFooterSubText={tableFooterSubText}
        tableFooterSubTextLink={tableFooterSubTextLink}
      />
    </Row>
  );
};

UniversalProductTable.propTypes = {
  // an id that is set on the topmost element - enables jump links to the table
  anchorId: PropTypes.string,
  // the bulk of the configuration for the table - an array of table columns
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      // the label for the column
      name: PropTypes.string,
      // elements represent components within the column. the shape of each element depends on the component it represents
      elements: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  // the list of any products that are featured
  featuredProducts: PropTypes.arrayOf(
    PropTypes.shape({
      // the id of the featured product
      id: PropTypes.string,
      // the label for the featured product display
      label: PropTypes.string,
    })
  ),
  // sub-header text above the table
  subtitle: PropTypes.string,
  // the type of the table to render, i.e. 'savings', 'checking' - one of our preset configurations or 'custom'
  tableType: PropTypes.string,
  // header text above the table
  title: PropTypes.string,
  dataDependency: PropTypes.shape({
    data: PropTypes.shape({
      ids: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    }),
    type: PropTypes.string,
    productPage: PropTypes.string,
  }),
  ...TableFooter.propTypes,
};

UniversalProductTable.defaultProps = {
  columns: [],
  featuredProducts: [],
  tableType: '',
  data: {},
  dataDependency: {},
};

// we need to keep the preset config in sync with the component's JSON
// so whenever tableType and columns get out of sync, update the component accordingly
UniversalProductTable.onUpdate = (componentDocument) => {
  const tableType = _.get(componentDocument, 'props.tableType');
  const filteredPreset = filterColumnConfig(getPresetColumnConfig(tableType));
  const currColumns = _.get(componentDocument, 'props.columns');
  const filteredColumns = filterColumnConfig(currColumns);
  // check and see if the table type and columns structure is out of sync
  // if they are, update the columns structure appropriately
  if (tableType !== 'custom' && !_.isEqual(filteredPreset, filteredColumns)) {
    // update component with preset config
    const newComponentDocument = {
      ...componentDocument,
      props: {
        ..._.get(componentDocument, 'props'),
        columns: filteredPreset,
      },
    };
    return newComponentDocument;
  }
  return componentDocument;
};

UniversalProductTable.getEditingConfig = (componentDocument) => {
  const tableTypes = Object.keys(configs)
    // testConfig is a config for unit tests, but shouldn't be available in the builder
    // investing is still WIP and will be removed from this filter when it's finished
    .filter((configName) => configName !== 'test' && configName !== 'investing')
    .map((configName) => ({
      label: _.startCase(configName),
      value: configName,
    }));
  tableTypes.unshift({ label: 'Pick a table type', value: undefined });
  // disable the ability to create custom tables for now
  // TODO: get decision from PM on whether or not we want to support custom table creation and
  // either support properly or remove extra functionality
  // tableTypes.push({ label: 'Custom table', value: 'custom' });

  const idList = _.get(componentDocument, 'dataDependency.data.ids', []).map(
    (idObj) => ({ label: idObj.id, value: idObj.id })
  );
  idList.unshift({ label: 'Choose an ID', value: undefined });

  const componentWhitelist = [
    { label: 'Choose an element', value: null },
    { label: 'Apply Now', value: 'ApplyNow' },
    { label: 'Collapsible Paragraph', value: 'CollapsibleParagraph' },
    { label: 'Label', value: 'Label' },
    { label: 'Quick Facts', value: 'EditableQuickFacts' },
    { label: 'Pros and Cons', value: 'ProsAndCons' },
    { label: 'Star Rating', value: 'StarRating' },
  ];

  const isCustomTable =
    _.get(componentDocument, 'props.tableType') === 'custom';
  const columnTitle = isCustomTable
    ? [stringInputSpec('name', 'Column Title')]
    : [];

  return [
    stringInputSpec('title', 'Title'),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    dataDependencyTypePicker,
    dataDependencyIdsAdder,
    repeatable(
      'featuredProducts',
      'Featured Products',
      'Featured Product',
      () => [
        stringSelectSpec('id', 'Product ID', idList),
        stringInputSpec('label', 'Label (defaults to "Featured")'),
      ]
    ),
    anchorIdInput,
    stringSelectSpec('tableType', 'Table type', tableTypes),
    repeatable(
      'columns',
      'Columns',
      'Column',
      (columnIndex) => [
        ...columnTitle,
        repeatable(
          'elements',
          'Elements',
          'Element',
          (elementIndex) => {
            const currElement = _.get(
              componentDocument,
              `props.columns[${columnIndex}].elements[${elementIndex}.type`
            );
            const Component = _.get(componentLibrary, currElement);
            const componentConfig =
              Component && typeof Component.getEditingConfig === 'function'
                ? Component.getEditingConfig()
                : [];

            return [
              stringSelectSpec(
                'type',
                'Element type',
                // if this isn't a custom table, just show user the name of the component they're editing
                !isCustomTable
                  ? componentWhitelist.filter(
                      (component) => component.value === currElement
                    )
                  : componentWhitelist
              ),
              ...componentConfig,
            ];
          },
          { repeatableIsEditable: isCustomTable }
        ),
      ],
      { repeatableIsEditable: isCustomTable }
    ),
    ...TableFooter.getEditingConfig(),
  ];
};

/**
 * Map ingested props to data dependent fields by way of Structured-Content Provider data descriptions.
 * @see {@link https://github.com/NerdWallet/nwjs-structured-content-core#datadependencies}
 *
 * @param {Object} props
 * @param {Object} props.dataDependency - data dependency specifications
 * @param {Object} props.location - location info passed by way of `withRouter` HOC.
 */
/* istanbul ignore next */
const dataDependenciesWithRoute = ({ dataDependency, location }) => {
  const { type } = dataDependency;
  const { pathname = '' } = location;
  if (!type) {
    return null;
  }

  return {
    data: {
      productPage: getSlug(pathname),
      ids: unpackDataDependencyIds(dataDependency),
      type,
    },
  };
};

export default withDataDependency(
  UniversalProductTable,
  dataDependenciesWithRoute
);
